import React, { useState } from 'react';
import { CheckCircle2, Circle, AlertTriangle, Clock, ChevronDown, ChevronUp } from 'lucide-react';
import { seoService } from '../../../services/seoService';
import { trackEvent } from '../../../hooks/useGoogleAnalytics';

const SEOChecklist = ({ auditItems, onUpdate }) => {
  const [expandedItem, setExpandedItem] = useState(null);
  const [filter, setFilter] = useState('all');

  const priorityColors = {
    critical: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
    high: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-200' },
    medium: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-200' },
    low: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-200' }
  };

  const statusIcons = {
    completed: CheckCircle2,
    in_progress: Clock,
    pending: Circle,
    failed: AlertTriangle
  };

  const filteredItems = filter === 'all' 
    ? auditItems 
    : auditItems?.filter(item => item?.priority === filter);

  const handleStatusUpdate = async (itemId, newStatus) => {
    try {
      await seoService?.updateAuditItem(itemId, { current_status: newStatus });
      trackEvent('seo_audit_update', { item_id: itemId, new_status: newStatus });
      onUpdate();
    } catch (error) {
      console.error('Error updating audit item:', error);
    }
  };

  const toggleExpand = (itemId) => {
    setExpandedItem(expandedItem === itemId ? null : itemId);
  };

  return (
    <div className="space-y-6">
      {/* Filter Buttons */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap gap-3">
          {['all', 'critical', 'high', 'medium', 'low']?.map((filterOption) => (
            <button
              key={filterOption}
              onClick={() => setFilter(filterOption)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === filterOption
                  ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filterOption?.charAt(0)?.toUpperCase() + filterOption?.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {/* Audit Items List */}
      <div className="space-y-4">
        {filteredItems?.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <Circle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">No audit items found</p>
          </div>
        ) : (
          filteredItems?.map((item) => {
            const StatusIcon = statusIcons?.[item?.current_status] || Circle;
            const priorityStyle = priorityColors?.[item?.priority] || priorityColors?.medium;
            const isExpanded = expandedItem === item?.id;

            return (
              <div
                key={item?.id}
                className={`bg-white rounded-lg shadow-sm border-l-4 ${priorityStyle?.border} overflow-hidden hover:shadow-md transition-shadow`}
              >
                {/* Header */}
                <div className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      <div className={`w-12 h-12 rounded-lg ${priorityStyle?.bg} flex items-center justify-center flex-shrink-0`}>
                        <StatusIcon className={`w-6 h-6 ${priorityStyle?.text}`} />
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-gray-900">{item?.audit_item}</h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${priorityStyle?.bg} ${priorityStyle?.text}`}>
                            {item?.priority}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2">{item?.audit_category}</p>
                        <p className="text-sm text-gray-500">{item?.page_url}</p>
                        
                        {item?.score !== null && (
                          <div className="mt-3">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-gray-600">Score</span>
                              <span className="font-bold text-gray-900">{item?.score}/100</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full transition-all ${
                                  item?.score >= 80 ? 'bg-green-600' :
                                  item?.score >= 60 ? 'bg-yellow-600' : 'bg-red-600'
                                }`}
                                style={{ width: `${item?.score}%` }}
                              ></div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => toggleExpand(item?.id)}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-gray-600" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>
                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-6 pb-6 pt-2 border-t border-gray-100">
                    <div className="space-y-4">
                      {item?.recommendation && (
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Recommendation</h4>
                          <p className="text-gray-700">{item?.recommendation}</p>
                        </div>
                      )}
                      
                      {item?.current_value && (
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Current Value</h4>
                          <p className="text-gray-700 font-mono text-sm bg-gray-50 p-3 rounded">{item?.current_value}</p>
                        </div>
                      )}
                      
                      {item?.target_value && (
                        <div>
                          <h4 className="font-medium text-gray-900 mb-2">Target Value</h4>
                          <p className="text-gray-700 font-mono text-sm bg-green-50 p-3 rounded">{item?.target_value}</p>
                        </div>
                      )}

                      {/* Status Update Buttons */}
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Update Status</h4>
                        <div className="flex flex-wrap gap-2">
                          {['pending', 'in_progress', 'completed', 'failed']?.map((status) => (
                            <button
                              key={status}
                              onClick={() => handleStatusUpdate(item?.id, status)}
                              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                item?.current_status === status
                                  ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              {status?.replace('_', ' ')}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="text-xs text-gray-500 pt-2">
                        Last checked: {new Date(item.last_checked)?.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SEOChecklist;